import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { clueCategories, discussions as initialDiscussions } from '@/mocks/homeData';

const avatarTone = [
  'bg-primary-500 text-background-50',
  'bg-accent-500 text-background-50',
  'bg-secondary-500 text-background-50',
  'bg-foreground-800 text-background-50',
];

export default function DiscussionSection() {
  const [list, setList] = useState(initialDiscussions);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [activeTag, setActiveTag] = useState('全部');

  const hotTags = ['全部', ...clueCategories.filter((c) => c.id !== 'all').map((c) => c.label)];

  const visible = activeTag === '全部' ? list : list.filter((item) => item.tag === activeTag);

  const handleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePublish = () => {
    const text = draft.trim();
    if (!text) {
      setNotice('先写点什么再发布吧');
      return;
    }
    setList((prev) => [
      {
        id: `local-${Date.now()}`,
        user: '我',
        avatar: '我',
        time: '刚刚',
        content: text,
        likes: 0,
        replies: 0,
        tag: activeTag === '全部' ? '资金往来' : activeTag,
      },
      ...prev,
    ]);
    setDraft('');
    setNotice('已记录到本地列表（演示用，不会真实提交）');
  };

  return (
    <section id="discussion" className="scroll-mt-20 border-b border-background-200 bg-background-100/50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          index="06"
          eyebrow="Discussion"
          title="讨论区"
          desc="看得见的推理过程比结论更有意思。这里汇集读者对资金往来、合同文件与内部邮件的新发现，欢迎补充你注意到的细节。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px] lg:gap-8">
          <div>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {hotTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors ${
                    activeTag === tag
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-3">
              {visible.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
                  <i className="ri-chat-3-line text-2xl leading-none text-foreground-300"></i>
                  <p className="mt-3 text-sm text-foreground-600">这个话题还没有发言，来当第一个</p>
                </div>
              ) : (
                visible.map((item) => (
                  <article
                    key={item.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${
                          avatarTone[item.user.length % avatarTone.length]
                        }`}
                      >
                        {item.avatar}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-foreground-950">{item.user}</span>
                          <span className="rounded-md bg-secondary-100 px-2 py-0.5 font-label text-[11px] text-secondary-900">
                            {item.tag}
                          </span>
                          <span className="font-label text-[11px] text-foreground-400">{item.time}</span>
                        </div>
                        <p className="mt-2 text-[13px] leading-6 text-foreground-700">{item.content}</p>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            type="button"
                            aria-pressed={Boolean(liked[item.id])}
                            onClick={() => handleLike(item.id)}
                            className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 font-label text-[11px] transition-colors ${
                              liked[item.id]
                                ? 'border-accent-300 bg-accent-50 text-accent-700'
                                : 'border-background-200 text-foreground-600 hover:border-foreground-300'
                            }`}
                          >
                            <i
                              className={`${liked[item.id] ? 'ri-heart-fill' : 'ri-heart-line'} text-sm leading-none`}
                            ></i>
                            {item.likes + (liked[item.id] ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="flex cursor-pointer items-center gap-1.5 rounded-full border border-background-200 px-3 py-1 font-label text-[11px] text-foreground-600 transition-colors hover:border-foreground-300"
                          >
                            <i className="ri-chat-1-line text-sm leading-none"></i>
                            回复 {item.replies}
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-chat-smile-2-line text-base leading-none"></i>
                </span>
                <span className="font-heading text-base font-bold text-foreground-950">参与讨论</span>
              </div>

              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="说说你在资金往来、合同文件或内部邮件里发现的新细节…"
                className="mt-4 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>

              <div className="mt-2 flex items-center justify-between">
                <span className="font-label text-[11px] text-foreground-400">{draft.length}/500</span>
                <button
                  type="button"
                  onClick={handlePublish}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-2-line text-base leading-none"></i>
                  发布看法
                </button>
              </div>

              {notice ? (
                <p className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-[12px] leading-5 text-foreground-600">
                  {notice}
                </p>
              ) : null}

              <div className="mt-5 border-t border-background-200 pt-4">
                <span className="font-label text-xs tracking-[0.18em] text-foreground-500">热门话题</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {clueCategories
                    .filter((c) => c.id !== 'all')
                    .map((c) => (
                      <span
                        key={c.id}
                        className="cursor-pointer rounded-md border border-background-200 bg-background-100 px-2.5 py-1 font-label text-[11px] text-foreground-700 transition-colors hover:border-foreground-300"
                      >
                        #{c.label}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}